/**
 * The full-page manual ticket form (`/tickets/new`): an agent logging a ticket
 * that arrived by phone, email, Teams or in person, or a customer filing one.
 * Pure: form state → validation → the API payloads and the follow-up steps.
 *
 * The API is the authority (POST /tickets validates everything again); these
 * rules only let the page point at a field before the round trip.
 */
import { RECEIVED_VIA, type CreateTicketInput, type ReceivedVia } from '@/api/ticketsApi'

export { RECEIVED_VIA, type ReceivedVia }

export const MANUAL_GXP_IMPACTS = ['unassessed', 'none', 'assessed_no_risk', 'yes'] as const

/** `agent`: full desk form. `customer`: simplified (no customer, reporter, assignee, note, GxP or intake fields). */
export type ManualTicketMode = 'agent' | 'customer'

export interface ManualTicketForm {
  customerId: string
  environmentId: string
  /** Agents: '' = from the reporter (the API defaults to the reporter contact's tenant), else a tenant id. */
  tenantId: string
  reporterMode: 'existing' | 'new'
  contactId: string
  newContactName: string
  newContactEmail: string
  newContactRole: string
  receivedVia: ReceivedVia
  /** `datetime-local` value (`YYYY-MM-DDTHH:mm`) in the agent's own time zone. */
  receivedAt: string
  productId: string
  componentId: string
  type: string
  priority: string
  subject: string
  description: string
  gxpImpact: string
  gxpNote: string
  formRef: string
  formVersion: string
  recordType: string
  recordKey: string
  assigneeId: string
  watcherContactIds: string[]
  internalNote: string
}

export function emptyManualTicket(receivedAt: string): ManualTicketForm {
  return {
    customerId: '',
    environmentId: '',
    tenantId: '',
    reporterMode: 'existing',
    contactId: '',
    newContactName: '',
    newContactEmail: '',
    newContactRole: '',
    receivedVia: 'phone',
    receivedAt,
    productId: '',
    componentId: '',
    type: 'defect',
    priority: 'P3',
    subject: '',
    description: '',
    gxpImpact: 'unassessed',
    gxpNote: '',
    formRef: '',
    formVersion: '',
    recordType: '',
    recordKey: '',
    assigneeId: '',
    watcherContactIds: [],
    internalNote: '',
  }
}

/** A `datetime-local` value for an instant, in the runtime's local zone. */
export function toLocalInput(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`
}

/** Parses a `datetime-local` value as local time; null when malformed. */
export function fromLocalInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]))
  return Number.isNaN(d.getTime()) || d.getMonth() !== Number(m[2]) - 1 ? null : d
}

// ─── Record keys (mirrors the API's RecordKeyPattern check) ──────────────────

export interface KeyPattern {
  type: string
  regex: string
  normalizer?: string
  urlTemplate?: string | null
}

/** Same normalizers as the API's record-key.ts. */
export function normalizeRecordKey(key: string, normalizer: string | undefined): string {
  const trimmed = key.trim()
  if (normalizer === 'strip_spaces_upper') return trimmed.replace(/[\s-]+/g, '').toUpperCase()
  if (normalizer === 'upper') return trimmed.toUpperCase()
  return trimmed
}

/** Sample keys for the formats the seed configures (shown when a key doesn't match). */
export const KEY_EXAMPLES: Record<string, string> = {
  din: 'W1234 26 004517',
  capa: 'CAPA-2026-0142',
  sop: 'SOP-QA-012',
  qc_run: 'QC-2026-00418',
}

/** An example of a valid key for the pattern, or the pattern itself. */
export function keyExample(pattern: KeyPattern): string {
  return KEY_EXAMPLES[pattern.type] ?? pattern.regex
}

export type KeyCheck = 'empty' | 'valid' | 'invalid' | 'unchecked'

/**
 * `unchecked` when the product has no pattern for the type (free text, the API
 * accepts it); otherwise the key must match as typed or once normalized.
 */
export function checkRecordKey(key: string, pattern: KeyPattern | undefined): KeyCheck {
  if (!key.trim()) return 'empty'
  if (!pattern) return 'unchecked'
  let re: RegExp
  try {
    re = new RegExp(pattern.regex)
  } catch {
    return 'unchecked'
  }
  return re.test(key.trim()) || re.test(normalizeRecordKey(key, pattern.normalizer)) ? 'valid' : 'invalid'
}

// ─── Validation ──────────────────────────────────────────────────────────────

export type ManualTicketField =
  | 'customerId'
  | 'contactId'
  | 'newContactName'
  | 'newContactEmail'
  | 'receivedAt'
  | 'productId'
  | 'subject'
  | 'description'
  | 'gxpNote'
  | 'formVersion'
  | 'recordKey'
  | 'recordType'

/** Error codes (the page maps them to i18n strings). */
export type ManualTicketErrorCode =
  | 'required'
  | 'email'
  | 'future'
  | 'invalid_date'
  | 'too_long'
  | 'record_key'
  | 'gxp_note'
  | 'form_version'

export type ManualTicketErrors = Partial<Record<ManualTicketField, ManualTicketErrorCode>>

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const LIMITS = { subject: 300, description: 20000, gxpNote: 4000, formRef: 100, formVersion: 50, name: 200 }

export function validateManualTicket(
  form: ManualTicketForm,
  mode: ManualTicketMode,
  ctx: { keyPatterns?: KeyPattern[]; now?: Date } = {},
): ManualTicketErrors {
  const e: ManualTicketErrors = {}
  const blank = (s: string) => !s.trim()
  if (mode === 'agent') {
    if (!form.customerId) e.customerId = 'required'
    if (form.reporterMode === 'existing') {
      if (!form.contactId) e.contactId = 'required'
    } else {
      if (blank(form.newContactName)) e.newContactName = 'required'
      else if (form.newContactName.trim().length > LIMITS.name) e.newContactName = 'too_long'
      if (blank(form.newContactEmail)) e.newContactEmail = 'required'
      else if (!EMAIL.test(form.newContactEmail.trim())) e.newContactEmail = 'email'
    }
    const at = fromLocalInput(form.receivedAt)
    if (!form.receivedAt) e.receivedAt = 'required'
    else if (!at) e.receivedAt = 'invalid_date'
    else if (at.getTime() > (ctx.now ?? new Date()).getTime() + 5 * 60_000) e.receivedAt = 'future'
    if ((form.gxpImpact === 'yes' || form.gxpImpact === 'assessed_no_risk') && blank(form.gxpNote)) e.gxpNote = 'gxp_note'
    else if (form.gxpNote.length > LIMITS.gxpNote) e.gxpNote = 'too_long'
  }
  if (!form.productId) e.productId = 'required'
  if (blank(form.subject)) e.subject = 'required'
  else if (form.subject.trim().length > LIMITS.subject) e.subject = 'too_long'
  if (blank(form.description)) e.description = 'required'
  else if (form.description.length > LIMITS.description) e.description = 'too_long'
  if (!blank(form.formVersion) && blank(form.formRef)) e.formVersion = 'form_version'
  if (!blank(form.recordKey) || !blank(form.recordType)) {
    if (blank(form.recordType)) e.recordType = 'required'
    const pattern = ctx.keyPatterns?.find((p) => p.type === form.recordType.trim())
    const check = checkRecordKey(form.recordKey, pattern)
    if (check === 'empty') e.recordKey = 'required'
    else if (check === 'invalid') e.recordKey = 'record_key'
  }
  return e
}

// ─── Tenant ──────────────────────────────────────────────────────────────────

/**
 * The tenant picker's value after the reporter changes: the chosen contact's
 * own tenant (when the customer still offers it), else '' — "from the
 * reporter", which the API resolves the same way.
 */
export function defaultTenantId(
  contact: { tenantId: string | null } | null | undefined,
  tenants: { id: string }[],
): string {
  const id = contact?.tenantId
  return id && tenants.some((t) => t.id === id) ? id : ''
}

// ─── Payload ─────────────────────────────────────────────────────────────────

/**
 * POST /tickets body. Agents: desk channel, customer + reporter (existing
 * contact, or `{ email, name, role }`, which the API matches or creates),
 * tenant, intake `context` (received via / at), GxP impact + note and
 * assignee. Customers: only what a reporter may set; the API fills the rest.
 */
export function toCreatePayload(
  form: ManualTicketForm,
  mode: ManualTicketMode,
  ctx: { productKey?: string; canAssign?: boolean },
): CreateTicketInput {
  const trim = (s: string) => s.trim()
  const opt = (s: string) => (trim(s) ? trim(s) : undefined)
  const input: CreateTicketInput = {
    subject: trim(form.subject),
    description: form.description.trimEnd(),
    type: form.type,
    priority: form.priority,
    productId: form.productId,
    componentId: opt(form.componentId),
    environmentId: opt(form.environmentId),
    channel: mode === 'agent' ? 'desk' : 'portal',
    formRef: opt(form.formRef),
    formVersion: opt(form.formRef) ? opt(form.formVersion) : undefined,
  }
  if (form.watcherContactIds.length) input.watcherContactIds = [...new Set(form.watcherContactIds)]
  if (trim(form.recordType) && trim(form.recordKey) && ctx.productKey)
    input.linkedRecord = { product: ctx.productKey, type: trim(form.recordType), key: trim(form.recordKey) }
  if (mode === 'agent') {
    input.customerId = form.customerId
    input.tenantId = opt(form.tenantId)
    input.reporter =
      form.reporterMode === 'existing'
        ? { contactId: form.contactId }
        : {
            email: trim(form.newContactEmail).toLowerCase(),
            name: trim(form.newContactName),
            ...(trim(form.newContactRole) ? { role: trim(form.newContactRole) } : {}),
          }
    const receivedAt = fromLocalInput(form.receivedAt)
    if (receivedAt) input.context = { receivedVia: form.receivedVia, receivedAt: receivedAt.toISOString() }
    if (form.gxpImpact && form.gxpImpact !== 'unassessed') {
      input.gxpImpact = form.gxpImpact
      input.gxpNote = opt(form.gxpNote)
    }
    if (ctx.canAssign && form.assigneeId) input.assigneeId = form.assigneeId
  }
  // Drop undefined keys so the body matches what the dialog sends.
  for (const k of Object.keys(input) as (keyof CreateTicketInput)[]) if (input[k] === undefined) delete input[k]
  return input
}

// ─── After create ────────────────────────────────────────────────────────────

export type FollowUpStep = 'attachments' | 'note'

/**
 * What runs after the ticket exists, in order: the intake files (attached to
 * the description message, `firstMessageId`), then the opening internal note.
 * The GxP note, intake context and a new contact's role travel in the create.
 */
export function followUpSteps(
  form: ManualTicketForm,
  mode: ManualTicketMode,
  ctx: { files: number; canWriteNotes: boolean },
): FollowUpStep[] {
  const steps: FollowUpStep[] = []
  if (ctx.files > 0) steps.push('attachments')
  if (mode === 'agent' && ctx.canWriteNotes && form.internalNote.trim()) steps.push('note')
  return steps
}

/** `@Name` mentions in a note, resolved against the agent list (longest names first). */
export function noteMentions(body: string, agents: { id: string; name: string }[]): string[] {
  const found = new Set<string>()
  let rest = body.toLowerCase()
  for (const a of [...agents].sort((x, y) => y.name.length - x.name.length)) {
    const tag = `@${a.name.toLowerCase()}`
    if (!a.name.trim() || !rest.includes(tag)) continue
    found.add(a.id)
    // A matched longer name is consumed so "@Rosa" doesn't also match "@Rosa Alvarez".
    rest = rest.split(tag).join(' ')
  }
  return [...found]
}

// ─── SLA hint ────────────────────────────────────────────────────────────────

/** "45m", "4h", "1d 2h" style budget text (calendar minutes as configured). */
export function formatBudget(mins: number): string {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
}
